import React from 'react';

import type { Map as MapboxMap } from 'mapbox-gl';
import { observer } from 'mobx-react-lite';
import MapboxWithServiceAreas from '@bringg/react-components/dist/components/mapbox/mapbox-with-service-areas/mapbox-with-service-areas';
import type { ServiceArea } from '@bringg/types';
import type { MapboxClusterData } from '@bringg/react-components/dist/types/maps.consts';
import classNames from 'classnames';

import type { UpdatingSource } from '../types';
import useStore from '../../stores/helpers/use-context';
import { DEFAULT_CLUSTER_CONFIGURATION } from '../routes-planner/routes-planner';
import MapActions from './map-actions';
import PlannerKPIs from '../planner-kpis/planner-kpis';

import './planner-map.scss';

interface Props {
	mapApiKey: string;
	mapZoom?: number;
	getAllServiceAreas: () => Promise<ServiceArea[]>;
	clusterConfig?: MapboxClusterData;
	onMapLoaded: (mapInstance?: MapboxMap) => void;
	isExpanded?: boolean;
	resizeMap?: () => void;
	changeTasksSelection: (tasksIds: number[], source: UpdatingSource) => void;
	isOptimizationKpiEnabled?: boolean;
	countryCode: string;
}

const MAPBOX_OPTIONS = {
	style: 'mapbox://styles/bringg/cl0taf34y00q014qbypr0bbm9'
};

const PlannerMap = (props: Props) => {
	const {
		getAllServiceAreas,
		mapApiKey,
		clusterConfig,
		mapZoom,
		onMapLoaded,
		isExpanded,
		resizeMap,
		isOptimizationKpiEnabled,
		countryCode
	} = props;

	const { translations, mapStore, team } = useStore();
	const { mapMarkers, mapPolylines, mapCenter } = mapStore;

	return (
		<div
			className={classNames('planner-map-container', { 'is-expanded': isExpanded })}
			onTransitionEnd={() => {
				resizeMap();
			}}
		>
			<MapActions changeTasksSelection={props.changeTasksSelection} />
			{isOptimizationKpiEnabled && (
				<PlannerKPIs
					countryCode={countryCode}
					translations={{
						...translations.kpisTranslations,
						...{
							miles: translations.routesTableTranslations.headers.miles,
							km: translations.routesTableTranslations.headers.km
						}
					}}
				/>
			)}
			<MapboxWithServiceAreas
				className="planner-map"
				apiKey={mapApiKey}
				center={mapCenter}
				markers={mapMarkers}
				polylines={mapPolylines}
				mapboxOptions={MAPBOX_OPTIONS}
				clusterData={clusterConfig ?? DEFAULT_CLUSTER_CONFIGURATION}
				zoom={mapZoom}
				getAllServiceAreas={getAllServiceAreas}
				editable={false}
				translations={translations.serviceAreasTranslations}
				shouldFitServiceAreasBounds={false}
				onLoaded={onMapLoaded}
				teamId={team?.id}
			/>
		</div>
	);
};

export default observer(PlannerMap);
